import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

class ProductInventory extends Component {
  #deliveryTimer;
  #deliveryMotionObserver;
  #deliveryMotionPlayed = false;

  connectedCallback() {
    super.connectedCallback();
    this.#updateDeliveryDate();
    this.#queueDeliveryMotion();
    document.addEventListener('visibilitychange', this.#updateDeliveryDate);
    window.addEventListener('pageshow', this.#updateDeliveryDate);
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#deliveryTimer);
    this.#deliveryMotionObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.#updateDeliveryDate);
    window.removeEventListener('pageshow', this.#updateDeliveryDate);
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  #queueDeliveryMotion() {
    this.#deliveryMotionObserver?.disconnect();
    if (this.#deliveryMotionPlayed || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const icon = this.querySelector('.product-inventory__delivery-icon');
    if (!icon) return;
    this.#deliveryMotionObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 1)) return;
      this.#deliveryMotionPlayed = true;
      icon.classList.add('mnv-delivery-motion');
      this.#deliveryMotionObserver.disconnect();
    }, { threshold: 1, rootMargin: '0px 0px -24px 0px' });
    this.#deliveryMotionObserver.observe(icon);
  }

  #updateDeliveryDate = () => {
    clearTimeout(this.#deliveryTimer);
    const dateElement = this.querySelector('[data-delivery-date]');
    if (!dateElement) return;
    const today = new Date();
    const days = Number(dateElement.dataset.deliveryDays) || 7;
    const delivery = new Date(today.getFullYear(), today.getMonth(), today.getDate() + days, 12);
    dateElement.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      month: 'short',
      day: 'numeric',
    }).format(delivery);
    dateElement.dateTime = [
      delivery.getFullYear(),
      String(delivery.getMonth() + 1).padStart(2, '0'),
      String(delivery.getDate()).padStart(2, '0'),
    ].join('-');
    const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    this.#deliveryTimer = setTimeout(this.#updateDeliveryDate, midnight.getTime() - today.getTime() + 1000);
  };

  /**
   * Handles product select event by updating the inventory.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newInventory = html.querySelector('product-inventory');
        if (!newInventory) return;

        morph(this, newInventory, { childrenOnly: true });
        this.#updateDeliveryDate();
        this.#queueDeliveryMotion();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-inventory] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
